/**
 * Soft-маска для гос. номера РФ. Не блокирует ввод нестандартных номеров —
 * просто форматирует то, что подходит под шаблон, а лишнее оставляет как есть.
 *
 * Логика (1:1 с мобильной частью `lib/core/utils/license_plate_formatter.dart`):
 *  - uppercase всех букв
 *  - латиница → кириллица для 12 букв-двойников (А В Е К М Н О Р С Т У Х)
 *  - пробелы по позициям: после 1-й буквы, 3-х цифр, 2-х букв → «А 123 ВС 77»
 *    или «А 123 ВС 777» (3-значный регион)
 *  - нестандартный ввод проходит без пробелов (uppercase + транслит сохраняются)
 *
 * Хранение в БД: без пробелов («А123ВС77»). Используйте `normalizeLicensePlate`
 * перед записью.
 */

/** Map латиница → кириллица для 12 букв-двойников. */
const LATIN_TO_CYRILLIC: Record<string, string> = {
  A: 'А', a: 'А',
  B: 'В', b: 'В',
  E: 'Е', e: 'Е',
  K: 'К', k: 'К',
  M: 'М', m: 'М',
  H: 'Н', h: 'Н',
  O: 'О', o: 'О',
  P: 'Р', p: 'Р',
  C: 'С', c: 'С',
  T: 'Т', t: 'Т',
  Y: 'У', y: 'У',
  X: 'Х', x: 'Х',
}

/** 12 разрешённых кириллических букв на рос. номерах. */
const ALLOWED_CYRILLIC = new Set([
  'А', 'В', 'Е', 'К', 'М', 'Н', 'О', 'Р', 'С', 'Т', 'У', 'Х',
])

const isDigit = (ch: string): boolean => ch >= '0' && ch <= '9'

/** Приводит символ к canonical: латиница→кириллица, uppercase прочего. */
function normalizeChar(ch: string): string {
  return LATIN_TO_CYRILLIC[ch] ?? ch.toUpperCase()
}

/**
 * Форматирует строку как гос. номер РФ. Идемпотентна: повторный вызов на уже
 * отформатированной строке даст тот же результат.
 */
export function formatLicensePlate(input: string): string {
  if (!input) return ''

  // 1) Канонизация без пробелов.
  let canon = ''
  for (const ch of input) {
    if (ch === ' ') continue
    canon += normalizeChar(ch)
  }
  if (!canon) return ''

  // 2) Лимит 9 символов без пробелов (1 + 3 + 2 + 3 макс).
  const limited = canon.length > 9 ? canon.slice(0, 9) : canon

  // 3) Вставляем пробелы по позиции; если шаблон нарушен — возвращаем canon
  // без пробелов.
  const sections: string[] = []
  let buf = ''
  let section = 0 // 0=letter1, 1=digits3, 2=letter2x2, 3=region2or3

  for (const ch of limited) {
    const digit = isDigit(ch)
    const letter = ALLOWED_CYRILLIC.has(ch)
    const other = !digit && !letter

    if (other) return limited

    switch (section) {
      case 0:
        if (!letter) return limited
        buf += ch
        sections.push(buf)
        buf = ''
        section = 1
        break
      case 1:
        if (!digit) return limited
        buf += ch
        if (buf.length === 3) {
          sections.push(buf)
          buf = ''
          section = 2
        }
        break
      case 2:
        if (!letter) return limited
        buf += ch
        if (buf.length === 2) {
          sections.push(buf)
          buf = ''
          section = 3
        }
        break
      case 3:
        if (!digit) return limited
        buf += ch
        break
    }
  }
  if (buf) sections.push(buf)
  return sections.join(' ')
}

/**
 * Готовит значение для записи в БД: убирает пробелы. Пустая строка → null.
 */
export function normalizeLicensePlate(input: string | null | undefined): string | null {
  if (input == null) return null
  const stripped = input.replace(/ /g, '').trim()
  return stripped === '' ? null : stripped
}
